"use client";

import { useEffect, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useAuth } from "@/lib/auth-context";
import { apiGet } from "@/lib/api";

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading, needsSetup } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const [schwabExpired, setSchwabExpired] = useState<string | null>(null);

  // The red Schwab banner is ONLY useful when no real-time data provider is
  // designated. If one is designated, suppress it everywhere — expired/other
  // tokens don't matter for data. null = not yet checked (suppress to avoid a
  // flash before we know).
  const [hasRealtimeProvider, setHasRealtimeProvider] = useState<boolean | null>(null);

  useEffect(() => {
    if (!loading && !user) {
      router.replace("/");
    }
  }, [loading, user, router]);

  useEffect(() => {
    if (!loading && user && needsSetup && pathname !== "/setup") {
      router.replace("/setup");
    }
  }, [loading, user, needsSetup, pathname, router]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail || "Schwab tokens expired";
      setSchwabExpired(detail);
    };
    window.addEventListener("schwab:expired", handler);
    return () => window.removeEventListener("schwab:expired", handler);
  }, []);

  useEffect(() => {
    if (loading || !user || needsSetup) return;
    let cancelled = false;
    apiGet<{ capability: string; tokenId: number }[]>("/schwab/preferences")
      .then((prefs) => {
        if (!cancelled) setHasRealtimeProvider(prefs.some((p) => p.capability === "realtime"));
      })
      .catch(() => {
        if (!cancelled) setHasRealtimeProvider(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loading, user, needsSetup]);

  if (loading || !user) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-100px)]">
        <p className="text-muted">Loading...</p>
      </div>
    );
  }

  if (needsSetup && pathname !== "/setup") {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-100px)]">
        <p className="text-muted">Redirecting to account setup...</p>
      </div>
    );
  }

  // Show only when a Schwab 401 happened AND no real-time data provider is
  // designated. A designated provider suppresses it on every page.
  const showSchwabBanner = !!schwabExpired && hasRealtimeProvider === false;

  return (
    <>
      {showSchwabBanner && (
        <div className="mx-4 mt-2 rounded-lg border-2 border-red-500 bg-red-500/10 px-4 py-3 flex items-center justify-between">
          <span className="text-sm text-red-400 font-medium">{schwabExpired}</span>
          <a href="/schwab" className="text-sm font-bold text-cyan hover:text-cyan/80 underline ml-4">
            Reconnect Schwab
          </a>
        </div>
      )}
      {children}
    </>
  );
}
